import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { css } from '@bd/bdui-styles';
import { colors } from '../../../styles';
import FileInput from '../FileInput';

const styles = css`
  .FormFileMultiple_inputGroup {
    display: flex;
    align-items: flex-end;
    &:not(:last-child) {
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid ${colors.brand3};
    }
    @media (min-width: 768px) {
      display: block;
      &:not(:last-child) {
        margin-bottom: 8px;
        padding-bottom: 0;
        border-bottom: none;
      }
    }
  }
  .FormFileMultiple_inputGroupInner {
    width: calc(100% - 106px);
    margin-right: 15px;
    @media (min-width: 768px) {
      display: inline-block;
      width: 567px;
      margin-right: 30px;
    }
    @media (min-width: 992px) {
      width: calc(50% - 15px);
      min-width: 480px;
    }
  }
  .FormFileMultiple_inputWrapper {
    text-indent: -5000px;
    overflow: hidden;
  }
  .FormFileMultiple_styledFileInput {
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    width: auto;
    height: 37px;
    padding: 0 16px;
    margin-bottom: 8px;
    border: 2px solid ${colors.brand2};
    border-radius: 5px;
    background-color: white;
    color: ${colors.brand2};
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 0.166667em;
    line-height: 1;
    text-transform: uppercase;
    cursor: pointer;
    @media (min-width: 768px) {
      height: 42px;
      padding: 0 26px;
      font-size: 13px;
      margin: 0 15px 0 0;
    }
  }
  .FormFileMultiple_styledFileInput-focus {
    outline: 2px dotted ${colors.brand2};
    outline-offset: 1px;
  }
  .FormFileMultiple_name {
    display: block;
    max-width: 100%;
    overflow: hidden;
    @media (min-width: 768px) {
      display: inline-block;
      vertical-align: middle;
      max-width: 386px;
    }
    @media (min-width: 992px) {
      max-width: calc(100% - 181px);
    }
  }
  .FormFileMultiple_name-none {
    color: ${colors.brand4};
    font-size: 14px;
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
  .FormFileMultiple_name-named {
    font-weight: 800;
    font-size: 12px;
    @media (min-width: 768px) {
      font-size: 14px;
    }
  }
  .FormFileMultiple_name-error {
    font-size: 14px;
    font-weight: 800;
    color: ${colors.error};
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
  .FormFileMultiple_addRemove {
    font-size: 12px;
    font-weight: 600;
    border-radius: 2px;
    position: relative;
    display: inline-block;
    cursor: pointer;
    &:focus {
      outline: 2px dotted ${colors.brand3};
      outline-offset: 1px;
    }
  }
  .FormFileMultiple_addRemove-remove {
    padding: 1px 6px 1px 20px;
    color: ${colors.brand4};
    background-color: white;
    border: 1px solid ${colors.brand4};
    &::before {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 8px;
      transform: translateY(-50%);
      background-color: ${colors.brand4};
      width: 6px;
      height: 2px;
    }
  }
  .FormFileMultiple_addBtnWrapper {
    margin-top: 16px;
  }
  .FormFileMultiple_addRemove-add {
    padding: 4px 10px 4px 26px;
    color: white;
    background-color: ${colors.brand2};
    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      left: 10px;
      background-color: white;
      width: 8px;
      height: 2px;
    }
    &::before {
      transform: translateY(-50%) rotateZ(90deg);
    }
    &::after {
      transform: translateY(-50%);
    }
  }
`;

class FileMultipleField extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeInput: undefined,
      files: [{ name: undefined, id: 0 }],
      fileCount: 0,
    };
  }

  getFileName = id => event => {
    const { files, fileCount } = this.state;
    const thisFile = files.find(file => file.id === id);

    if (event) {
      const fileName = get(event, 'target.files[0].name');
      thisFile.name = fileName;

      if (fileCount === 0) {
        // Fix bug whereby selecting file A, removing file A, then reselecting file A
        // would not work – no file would be uploaded the second time – because the
        // underlying html input's value had not changed, even though we removed its
        // value from our form values.
        //
        // This problem only exists for the first file input b/c second and subsequent
        // file inputs are removed from the DOM when you hit remove file, so a new input
        // (with no value) must be re-added to the DOM before you could reselect file A.

        event.target.value = null; // eslint-disable-line no-param-reassign
      }

      if (thisFile.error) {
        thisFile.error = undefined;
      }
    } else {
      thisFile.error = true;
    }

    this.setState({
      files,
    });
  }

  addFile = () => {
    const nextCount = this.state.fileCount + 1;
    this.setState({
      files: [...this.state.files, { name: undefined, id: nextCount }],
      fileCount: nextCount,
    });
  }

  removeFile = index => {
    if (this.state.files.length === 1) {
      this.setState({
        files: [{ name: undefined, id: 0 }],
        fileCount: 0,
      });
    } else {
      const { files } = this.state;
      files.splice(index, 1);

      this.setState({
        files,
      });
    }
  }

  handleActiveInput = id => {
    this.setState({
      activeInput: id,
    });
  }

  render() {
    const {
      className,
      field,
      label,
      getError,
      formApi,
      required,
      max,
      additionalText,
    } = this.props;

    const { activeInput, files } = this.state;
    return (
      <div className={[styles, className].filter(v => v).join(' ')}>
        <div className="form-col-fluid">
          <label htmlFor="fileUpload" className="FormFileMultiple_label">
            {label}
            {
            required &&
            <span className="required"> *</span>
            }
            {
              additionalText &&
              <p className="TextField_additionalText">{additionalText}</p>
            }
          </label>
          <div>
            {
              files.map((file, index) => {
                const fileId = file.id;
                const fileName = `${field}-${fileId}`;
                return (
                  <div className="FormFileMultiple_inputGroup" key={`file-${fileId}`}>
                    <div className="FormFileMultiple_inputGroupInner">
                      <label
                        className={`FormFileMultiple_styledFileInput ${activeInput === fileId ? 'FormFileMultiple_styledFileInput-focus' : ''}`}
                        htmlFor={`FormFileMultiple_${fileName}`}
                      >
                        Choose File
                        <div className="FormFileMultiple_inputWrapper">
                          <FileInput
                            isRequired={required}
                            id={`FormFileMultiple_${fileName}`}
                            className="FormFileMultiple_input"
                            accept=".pdf,.doc"
                            field={fileName}
                            name={fileName}
                            onChange={this.getFileName(fileId)}
                            onKeyUp={e => {
                              if (e.key === 'Tab') {
                                this.handleActiveInput(fileId);
                              }
                            }}
                            onBlur={() => this.handleActiveInput()}
                          />
                        </div>
                      </label>
                      <span className={[
                        'FormFileMultiple_name',
                        file.error ? 'FormFileMultiple_name-error' : '',
                        file.name ? 'FormFileMultiple_name-named' : 'FormFileMultiple_name-none',
                      ].join(' ')}
                      >
                        {file.error ? 'Max file size 3MB. Try a smaller image.' : file.name || 'No file chosen'}
                      </span>
                    </div>
                    {
                      (file.name || (!file.name && index > 0)) &&
                        <span
                          className="FormFileMultiple_addRemove FormFileMultiple_addRemove-remove"
                          role="button"
                          tabIndex="0"
                          onClick={() => {
                            this.removeFile(index);
                            formApi.setValue(fileName, undefined);
                          }}
                          onKeyUp={e => {
                            if (e.key === 'Enter') {
                              this.removeFile(index);
                              formApi.setValue(fileName, undefined);
                            }
                          }}
                        >remove file</span>
                    }
                    {
                      getError(fileName) &&
                      <div className="invalid-feedback">
                        {getError(fileName)}
                      </div>
                    }
                  </div>
                );
              })
            }
          </div>
          {
            files.length < 5 && max > 1 &&
            <div className="FormFileMultiple_addBtnWrapper">
              <span
                className="FormFileMultiple_addRemove FormFileMultiple_addRemove-add"
                role="button"
                tabIndex="0"
                onClick={() => this.addFile()}
                onKeyUp={e => {
                  if (e.key === 'Enter') {
                    this.addFile();
                  }
                }}
              >add file</span>
            </div>
          }
        </div>
      </div>
    );
  }
}

FileMultipleField.propTypes = {
  className: PropTypes.string,
  field: PropTypes.string.isRequired,
  additionalText: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  getError: PropTypes.func.isRequired,
  formApi: PropTypes.object.isRequired,
  max: PropTypes.number,
};

FileMultipleField.defaultProps = {
  className: undefined,
  label: undefined,
  placeholder: undefined,
  required: false,
  requiredMessage: undefined,
  additionalText: undefined,
  max: 5,
};

export default FileMultipleField;



// WEBPACK FOOTER //
// ./src/components/Forms/Fields/FileMultiple.js